import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';

const CustomerDetailPage = ({ sess = { uid: '', role: 'user' } }) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch('/chatbot/api/v1.0/get/customer-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: sess.uid, customerId }),
      });
      const result = await response.json();

      if (result?.data) {
        setCustomerData(result.data);
      } else {
        setError('Customer not found');
      }
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
      </Stack>
    );
  }

  if (!customerData) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Typography variant="h5">Customer Details</Typography>
        <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/customers')}>
          Back
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Activity" value="activity" />
          </Tabs>

          {activeTab === 'overview' && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Stack spacing={1}>
                  <Typography><strong>Name:</strong> {customerData.name || 'N/A'}</Typography>
                  <Typography><strong>Phone:</strong> {customerData.phone || 'N/A'}</Typography>
                  <Typography><strong>Email:</strong> {customerData.email || 'N/A'}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography><strong>Tags:</strong> {customerData.tags || 'N/A'}</Typography>
                  <Typography><strong>Created:</strong> {formatDate(customerData.createdAt)}</Typography>
                  <Typography><strong>Last Contact:</strong> {formatDate(customerData.lastContact)}</Typography>
                </Stack>
              </Stack>
            </Stack>
          )}

          {activeTab === 'activity' && (
            <Stack spacing={1}>
              <Typography variant="h6">Recent Activity</Typography>
              <Typography color="text.secondary">Activity history will be displayed here.</Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerDetailPage;
